<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import api from '@/api';
import ItemList from '@/components/menu/common/item-list.vue';
import Item from '@/components/menu/common/item.vue';
import SectionWrapper from '@/components/menu/sections/section-wrapper.vue';
import { SharedType } from '@/types';
import { SurveySessionResponse } from '@/types/index';
import { sliceByCodePoints } from '@/utils/string';
import { useShareSurveys, useUserInfo } from '@/utils/swr';
import { userSession } from '@/utils/userSession';
import { useEmitter } from '@/utils/vue';
import { featureFlags } from '@/featureFlags';

const PAGE_NUMBER = 1;
const SURVEYS_LIMIT = 100;
const SHARE_SURVEYS_LIMIT = 5;
const MAX_ITEM_NAME_LENGTH = 100;
const emitter = useEmitter();
const { data: userInfo } = useUserInfo();
const userId = computed(() => userInfo.value?.id);

const surveys = ref<Array<SurveySessionResponse>>([]);

const truncateText = (text: string, maxLength: number) =>
  [...text].length <= maxLength
    ? text
    : sliceByCodePoints(text, 0, maxLength) + '⋯';

const shareSurveysList = computed(
  () =>
    shareSurveysSearchResponse?.data.value?.share_surveys.map(shareSurvey => ({
      id: shareSurvey.uuid,
      name: featureFlags.ANDEV_10336_RAISE_SURVEY_PROMPT_LIMIT
        ? truncateText(shareSurvey.title, MAX_ITEM_NAME_LENGTH)
        : shareSurvey.title,
      icon: 'share-thread' as const,
      route: {
        name: 'shareSurvey',
        params: { uuid: shareSurvey.uuid },
      },
    })) ?? [],
);

const surveysList = computed(() => {
  return surveys.value.map(survey => ({
    id: survey.session_id,
    name: featureFlags.ANDEV_10336_RAISE_SURVEY_PROMPT_LIMIT
      ? truncateText(survey.title, MAX_ITEM_NAME_LENGTH)
      : survey.title,
    icon: 'thread' as const,
    route: {
      name: 'surveySession',
      params: { survey_session_id: survey.session_id },
    },
  }));
});

/**
 * スレッド (surveys) の一覧を取得
 *
 * 2024-11-14現在 スレッドの上限は100件 かつ 取得するデータ項目もtitle, updated_at, uuidの3つと少ないため、ページング処理は不要 (考慮事項が増えるので、必要になるまで実装しない)
 * ※ APIとしてはページング処理ができるようになっている
 */
const fetchSurveys = async () => {
  surveys.value = await api.fetchSurveys(PAGE_NUMBER, SURVEYS_LIMIT);
};

const fetchShareSurveys = () => {
  const keyword = ref<undefined>();
  const sampleOnly = ref(false);
  const excludeOwner = ref(false);
  const shared = ref<SharedType>('both');
  const startDate = ref<undefined>();
  const endDate = ref<undefined>();
  const pageNumber = ref(PAGE_NUMBER);

  return useShareSurveys(
    keyword,
    userId,
    sampleOnly,
    excludeOwner,
    shared,
    startDate,
    endDate,
    pageNumber,
    SHARE_SURVEYS_LIMIT,
  );
};
const shareSurveysSearchResponse = fetchShareSurveys();

onMounted(() => {
  fetchSurveys();
  // 要約メニューで 「新規スレッド作成」 or 「既存スレッド更新」 が行われた場合、スレッド一覧を更新する
  emitter.on(
    'update-survey-session',
    (updatedSurvey: SurveySessionResponse) => {
      const existingIndex = surveys.value.findIndex(
        survey => survey.session_id === updatedSurvey.session_id,
      );

      if (existingIndex !== 0) fetchSurveys();
    },
  );
  emitter.on('delete-survey-session', fetchSurveys);
  emitter.on('update-share-survey', () => {
    shareSurveysSearchResponse?.mutate();
  });
});

const isShareSurveysMenuExpanded = computed({
  get: () => userSession.getSurveyMenuIsExpanded('shareSurveys'),
  set: value => userSession.setSurveyMenuIsExpanded('shareSurveys', value),
});

const isSurveysMenuExpanded = computed({
  get: () => userSession.getSurveyMenuIsExpanded('surveys'),
  set: value => userSession.setSurveyMenuIsExpanded('surveys', value),
});
</script>
<template>
  <SectionWrapper>
    <div class="section">
      <Item
        icon="sparkles-fill"
        id="surveyTop"
        name="要約生成"
        :route="{ name: 'surveyTop' }"
      />
    </div>
    <div class="section">
      <div class="survey-menu-title c-title-bold--s">シェアスレッド</div>
      <Item
        name="すべてのシェアスレッド"
        id="shareSurveys"
        :route="{ name: 'searchShareSurvey' }"
      />
      <ItemList
        v-if="shareSurveysList.length > 0"
        title="最近の項目"
        :items="shareSurveysList"
        :is-foldable="true"
        :initial-is-open="isShareSurveysMenuExpanded"
        @menu-list-is-open="isShareSurveysMenuExpanded = $event"
        class="survey-menu-list"
      />
    </div>
    <div class="section">
      <div class="survey-menu-title c-title-bold--s">スレッド</div>
      <Item
        name="すべてのスレッド"
        id="surveys"
        :route="{ name: 'searchSurvey' }"
      />
      <ItemList
        v-if="surveysList.length > 0"
        title="最近の項目"
        :items="surveysList"
        :is-foldable="true"
        :initial-is-open="isSurveysMenuExpanded"
        @menu-list-is-open="isSurveysMenuExpanded = $event"
        class="survey-menu-list"
      />
    </div>
  </SectionWrapper>
</template>
<style scoped lang="scss">
// SectionWrapperコンポーネントのスタイルを上書き
.section-wrapper {
  gap: 8px;
  padding-left: 0;
  padding-right: 0;
  color: $color-gray1000;
}
.section {
  padding: 0 24px 24px 24px;
  border-bottom: 1px solid $color-gray400;
}

.survey-menu-title {
  height: 24px;
  margin-top: 16px;
  padding: 0 8px;
}

.survey-menu-list {
  margin: 12px 0 8px;
}

.list-sub-title {
  padding: 8px 4px;
}
</style>
